<template>
  <div class="agreement-detail-page">
    <AppSectionHero
      title="Agreement Details"
      subtitle="Read the full agreement content and key workflow details on one page."
      :show-refresh="false"
      :stats="heroStats"
    />

    <div v-if="loading" class="agreement-detail-page__empty">
      Loading agreement details...
    </div>

    <div v-else-if="!agreement" class="agreement-detail-page__empty">
      Agreement details are not available for this record.
    </div>

    <div v-else class="agreement-detail-page__layout">
      <CCard class="border-0 shadow-sm agreement-detail-card agreement-detail-card--meta">
        <CCardBody>
          <div class="meta-title">Agreement Information</div>
          <div class="meta-list">
            <div class="meta-row">
              <span>ID</span>
              <strong>{{ agreement.documentCode || agreement.id }}</strong>
            </div>
            <div class="meta-row">
              <span>Title</span>
              <strong>{{ agreement.title }}</strong>
            </div>
            <div class="meta-row">
              <span>Type</span>
              <strong>{{ agreement.type }}</strong>
            </div>
            <div class="meta-row">
              <span>Partner</span>
              <strong>{{ agreement.partner }}</strong>
            </div>
            <div class="meta-row">
              <span>Department</span>
              <strong>{{ agreement.department }}</strong>
            </div>
            <div class="meta-row">
              <span>Status</span>
              <strong>
                <span class="status-badge" :class="getDisplayStatusClass(agreement)">
                  {{ displayStatus(agreement) }}
                </span>
              </strong>
            </div>
            <div class="meta-row">
              <span>Created By</span>
              <strong>{{ agreement.createdBy || '-' }}</strong>
            </div>
            <div class="meta-row">
              <span>Submitted At</span>
              <strong>{{ formatDate(agreement.submittedAt) }}</strong>
            </div>
            <div class="meta-row">
              <span>Current Stage</span>
              <strong>{{ agreement.currentStage || '-' }}</strong>
            </div>
            <div class="meta-row">
              <span>Committees</span>
              <strong>{{ formatCommittees(agreement.committees) }}</strong>
            </div>
            <div class="meta-row">
              <span>Start Date</span>
              <strong>{{ formatDate(agreement.startDate) }}</strong>
            </div>
            <div class="meta-row">
              <span>End Date</span>
              <strong>{{ formatDate(agreement.endDate) }}</strong>
            </div>
          </div>

          <div class="meta-actions">
            <CButton color="secondary" variant="outline" @click="$router.push('/agreements')">
              Back to Agreements
            </CButton>
            <CButton color="primary" @click="openReview">
              Open Review
            </CButton>
          </div>
        </CCardBody>
      </CCard>

      <CCard class="border-0 shadow-sm agreement-detail-card agreement-detail-card--document">
        <CCardBody>
          <div class="document-header">
            <div class="document-kicker">Full Agreement</div>
            <AgreementDocumentHeader
              class="document-header__branding"
              :title="agreement.title"
              :partner="agreement.partner"
              :partner-logo="agreement.partnerLogo"
              :agreement-type="agreement.type"
              :country="agreement.country"
              title-label="Agreement Title"
              compact
              :show-meta="true"
            />
          </div>

          <div v-if="documentSections.length" class="document-sections">
            <section
              v-for="section in documentSections"
              :key="section.key"
              class="document-section"
              :class="{ 'document-section--centered': isCenteredDocumentSection(section) }"
            >
              <h3 v-if="sectionDisplayTitle(section)">{{ sectionDisplayTitle(section) }}</h3>
              <AgreementSignaturePreview
                v-if="isSignatureDocumentSection(section)"
                :section="section"
                left-header="Mae Fah Luang University"
                :right-header="[agreement && agreement.partner, agreement && agreement.country].filter(Boolean).join('\n')"
              />
              <div v-else class="document-section__content">{{ section.content }}</div>
            </section>
          </div>

          <div v-else class="document-empty">
            No full agreement content is available for this record yet.
          </div>

          <div class="document-actions">
            <CButton v-if="canDownloadAgreementDocx" color="primary" @click="downloadAgreement('docx')">
              Download DOCX
            </CButton>
            <CButton v-if="canDownloadAgreementPdf" color="secondary" variant="outline" @click="downloadAgreement('pdf')">
              Download PDF
            </CButton>
          </div>
        </CCardBody>
      </CCard>
    </div>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import Service from '@/service/api'
import {
  getAgreementSectionDisplayTitle,
  isCenteredAgreementSection,
  isSignatureAgreementSection,
  normalizeAgreementSections
} from '@/utils/agreement-preview-sections'
import { downloadAgreementAs } from '@/utils/agreement-download'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'
import AgreementDocumentHeader from '@/views/shared/AgreementDocumentHeader'
import AgreementSignaturePreview from '@/views/shared/AgreementSignaturePreview'

function getStageLabel(item) {
  if (item && item.stageLabel) return item.stageLabel
  return item && isReadyForAdminFinalApproval(item)
    ? 'Waiting for approval'
    : item && Math.min(Number(item.currentStep || 1), 3) === 3
      ? 'Committee Review and Comments'
      : item && Math.min(Number(item.currentStep || 1), 3) === 2
        ? 'Assign Committee'
        : 'Draft Submitted'
}

export default {
  name: 'AdminAgreementDetailPage',
  components: {
    AppSectionHero,
    AgreementDocumentHeader,
    AgreementSignaturePreview
  },
  data() {
    return {
      agreement: null,
      loading: false
    }
  },
  computed: {
    agreementId() {
      return this.$route.params.id
    },
    heroStats() {
      if (!this.agreement) return []
      return [
        { label: 'Status', value: this.displayStatus(this.agreement), hint: 'Current agreement status', icon: 'cil-check-circle', iconClass: 'app-section-stat__icon--active' },
        { label: 'Stage', value: this.compactStageLabel, hint: 'Workflow progress', icon: 'cil-layers', iconClass: 'app-section-stat__icon--attention' },
        { label: 'Partner', value: this.agreement.partner || '-', hint: 'Partner institution', icon: 'cil-building', iconClass: 'app-section-stat__icon--total' }
      ]
    },
    compactStageLabel() {
      const stage = String(this.agreement && this.agreement.currentStage ? this.agreement.currentStage : '').trim()
      if (!stage) return '-'
      if (stage === 'Waiting for approval' || stage === 'Awaiting Admin Final Decision') return 'Admin Final Review'
      if (stage === 'Committee Review and Comments') return 'Committee Review'
      return stage
    },
    documentSections() {
      if (!this.agreement) return []
      const draftPreviewSections = normalizeAgreementSections(this.agreement.draftPreviewSections)
      if (draftPreviewSections.length) {
        return draftPreviewSections
          .map((item, index) => ({
            key: String(item && item.key ? item.key : `section-${index + 1}`),
            title: String(item && item.title ? item.title : `Section ${index + 1}`),
            content: String(item && item.content ? item.content : '')
          }))
          .filter((item) => item.content)
      }

      const articleDrafts = normalizeAgreementSections(this.agreement.articleDrafts)
      if (articleDrafts.length) {
        return articleDrafts
          .map((item, index) => ({
            key: String(item && item.key ? item.key : `article-${index + 1}`),
            title: String(item && item.title ? item.title : `Article ${index + 1}`),
            content: String(item && item.content ? item.content : '')
          }))
          .filter((item) => item.content)
      }

      const customDocument = this.agreement.customDocument
      if (customDocument && customDocument.content) {
        return [{
          key: 'custom-document',
          title: customDocument.title || 'Agreement Content',
          content: customDocument.content
        }]
      }

      if (this.agreement.summary) {
        return [{
          key: 'summary',
          title: 'Agreement Summary',
          content: this.agreement.summary
        }]
      }

      return []
    },
    canDownloadAgreementDocx() {
      return !!(this.agreement && (
        this.documentSections.length
        || (this.agreement.finalDocument && this.agreement.finalDocument.url)
        || (this.agreement.uploadedDraft && this.agreement.uploadedDraft.url)
      ))
    },
    canDownloadAgreementPdf() {
      return !!(this.agreement && this.documentSections.length)
    }
  },
  mounted() {
    this.loadAgreement()
  },
  methods: {
    normalizeAgreement(item) {
      return {
        ...item,
        documentCode: item && item.documentCode ? item.documentCode : item && item.id ? `MOU-${item.id}` : '',
        currentStage: getStageLabel(item),
        startDate: item && (item.startDate || item.submittedAt) ? item.startDate || item.submittedAt : '',
        endDate: item && (item.endDate || item.approvedAt) ? item.endDate || item.approvedAt : ''
      }
    },
    async loadAgreement() {
      if (!this.agreementId) return
      this.loading = true
      try {
        const response = await Service.adminReview('detail', { id: this.agreementId })
        const record = response && response.data && response.data.data ? response.data.data : null
        this.agreement = record ? this.normalizeAgreement(record) : null
      } catch (error) {
        this.agreement = null
      } finally {
        this.loading = false
      }
    },
    openReview() {
      if (!this.agreementId) return
      this.$router.push({ path: '/review', query: { agreementId: this.agreementId } })
    },
    sectionDisplayTitle(section) {
      return getAgreementSectionDisplayTitle(section)
    },
    isCenteredDocumentSection(section) {
      return isCenteredAgreementSection(section, this.agreement && this.agreement.type)
    },
    isSignatureDocumentSection(section) {
      return isSignatureAgreementSection(section)
    },
    downloadAgreement(format = 'docx') {
      if (!this.agreement) return
      downloadAgreementAs(this.agreement, this.documentSections, format)
    },
    getStatusClass(status) {
      return {
        approved: 'status-active',
        pending: 'status-pending',
        draft: 'status-draft',
        revision: 'status-expiring',
        expiring: 'status-expiring'
      }[status] || ''
    },
    isExpiringSoon(item) {
      if (!(item && item.endDate)) return false
      const now = new Date()
      const end = new Date(item.endDate)
      if (Number.isNaN(end.getTime())) return false
      const diff = end.getTime() - now.getTime()
      const days = diff / (1000 * 60 * 60 * 24)
      return days >= 0 && days <= 30
    },
    getDisplayStatusClass(item) {
      if (this.isExpiringSoon(item)) return 'status-expiring'
      return this.getStatusClass(item && item.status)
    },
    displayStatus(item) {
      if (this.isExpiringSoon(item)) return 'Expiring Soon'
      return {
        approved: 'Approved',
        pending: 'Pending Review',
        draft: 'Draft',
        revision: 'Revision Required',
        expiring: 'Expiring Soon'
      }[item && item.status] || (item && item.status) || '-'
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatCommittees(items) {
      return Array.isArray(items) && items.length ? items.join(', ') : '-'
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("agreement-detail-page");

.agreement-detail-page {
  margin: 0 auto;
  max-width: 1400px;
  padding: 30px;
}

::v-deep .app-section-stat {
  height: 100%;
}

::v-deep .app-section-stat__value {
  font-size: 1.5rem;
  min-height: 3.2rem;
}

::v-deep .app-section-stat__hint {
  min-height: 2rem;
}

.agreement-detail-page__layout {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
}

.agreement-detail-page__empty {
  background: #fff;
  border: 1px solid #e7edf4;
  border-radius: 20px;
  box-shadow: 0 14px 30px rgba(44, 52, 71, 0.06);
  color: #64748b;
  padding: 24px;
}

.agreement-detail-card {
  border-radius: 20px;
}

.agreement-detail-card--meta {
  height: fit-content;
}

.meta-title {
  color: #344767;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.meta-list {
  display: grid;
}

.meta-row {
  align-items: flex-start;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 0;
}

.meta-row:last-child {
  border-bottom: 0;
}

.meta-row span {
  color: #64748b;
}

.meta-row strong {
  color: #1f2937;
  flex: 1;
  font-weight: 600;
  text-align: right;
}

.meta-actions {
  flex-wrap: wrap;
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.agreement-detail-card--document {
  min-height: 720px;
}

.document-header {
  border-bottom: 1px solid #edf2f7;
  margin-bottom: 20px;
  padding-bottom: 20px;
}

.document-header__branding {
  margin-top: 14px;
}

.document-kicker {
  color: #9E212F;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.document-sections {
  display: grid;
  gap: 16px;
}

.document-section {
  background: #fff;
  border: 1px solid #e7edf4;
  border-radius: 18px;
  padding: 20px;
}

.document-section h3 {
  color: #344767;
  font-size: 1.02rem;
  font-weight: 700;
  margin: 0 0 12px;
}

.document-section__content {
  color: #334155;
  line-height: 1.75;
  white-space: pre-wrap;
}

.document-section--centered .document-section__content {
  text-align: center;
}

.document-empty {
  color: #64748b;
  padding: 20px 0;
}

.document-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 24px;
}

.status-badge {
  border-radius: 30px;
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 12px;
}

.status-active {
  background: #dcfce7;
  color: #166534;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-draft {
  background: #e2e3e5;
  color: #383d41;
}

.status-expiring {
  background: #ffe5e5;
  color: #b91c1c;
}

@media (max-width: 1024px) {
  .agreement-detail-page {
    padding: 20px;
  }

  .agreement-detail-page__layout {
    grid-template-columns: 1fr;
  }
}
</style>
